import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Native scroll-snap carousel state: tracks the centered slide and scrolls
 * a given slide to the center of the track. No loop, no autoplay.
 */
export function useSnapCarousel(count: number) {
  const trackRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLElement | null)[]>([])
  const [active, setActive] = useState(0)

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const scrollTo = useCallback(
    (index: number) => {
      const track = trackRef.current
      const slide = slideRefs.current[index]
      if (!track || !slide) return
      const clamped = Math.max(0, Math.min(count - 1, index))
      const target = slideRefs.current[clamped] ?? slide
      track.scrollTo({
        left: target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      })
    },
    [count],
  )

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    let frame = 0
    const update = () => {
      const center = track.scrollLeft + track.clientWidth / 2
      let nearest = 0
      let best = Infinity
      slideRefs.current.forEach((el, i) => {
        if (!el) return
        const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center)
        if (d < best) {
          best = d
          nearest = i
        }
      })
      setActive(nearest)
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }
    track.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    update()
    return () => {
      cancelAnimationFrame(frame)
      track.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  const setSlideRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      slideRefs.current[index] = el
    },
    [],
  )

  return { trackRef, setSlideRef, active, scrollTo }
}
